import React from 'react';
import { Archive, FileCheck2, ScrollText, Lock } from 'lucide-react';

const YearEndArchive = () => {
  return (
    <section id="year-end" className="my-20">
      <div className="bg-gradient-to-br from-apple-800/60 to-apple-900/60 backdrop-blur-md rounded-3xl border border-apple-700/40 p-8 sm:p-12 relative overflow-hidden">
        <div className="absolute -left-32 -top-32 w-72 h-72 rounded-full bg-sortio-primary/10 blur-[100px]" />
        <div className="absolute -right-32 -bottom-32 w-72 h-72 rounded-full bg-sortio-secondary/10 blur-[100px]" />

        <div className="relative z-10">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 rounded-full bg-sortio-primary/15 border border-sortio-primary/30 flex items-center justify-center mr-4">
              <Archive className="text-sortio-primary" size={22} />
            </div>
            <span className="text-xs font-medium uppercase tracking-wider text-sortio-primary">
              Year-end archive prep
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl font-semibold mb-6 tracking-tight text-apple-50 max-w-3xl">
            Close 2025, start 2026 in fifteen minutes
          </h2>

          <div className="max-w-3xl space-y-5 text-apple-200 leading-relaxed">
            <p>
              When you finish a client's return, the Rule Builder packs that tax year into a clean
              read-only archive: receipts split by month, 1099s grouped by issuer, brokerage
              statements ordered by date, payroll filings by quarter, and the working drafts moved
              into a "superseded" subfolder. The new year starts with an empty working tree and
              every prior year is reproducible.
            </p>
            <p>
              IRS retention is three to seven years depending on the situation. Sortio tags each
              year's archive with a retention class so when you re-open it for an amendment or a
              CP2000 notice, you find the exact layout you had at filing time. Nothing has been
              renamed or reshuffled after the fact.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-10">
            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <Lock className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Reproducible archives</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                A closed year archive is locked, named with filing date and form type, and the
                audit log captures every file movement that produced it. Three years later you can
                still answer the IRS.
              </p>
            </div>

            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <FileCheck2 className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">A local option for sensitive data</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                Run Sortio with a local LLM (Ollama) and receipts and statements with bank account
                numbers and Social Security info never leave your machine; even filenames stay
                on-device. By default Sortio uses our cloud, which sees filenames and metadata, and
                file contents only if you enable a content-based feature.
              </p>
            </div>

            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <ScrollText className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Audit log per move</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                Every file Sortio renames or routes is logged with timestamp, source path, and
                destination. If a partner asks "where did this expense come from", you can answer
                in under a minute.
              </p>
            </div>

            <div className="bg-apple-800/40 rounded-2xl border border-apple-700/50 p-6">
              <div className="flex items-center mb-3">
                <Archive className="text-sortio-primary mr-2" size={18} />
                <h3 className="text-base font-medium text-apple-50">Multi-year reproducibility</h3>
              </div>
              <p className="text-sm text-apple-200 leading-relaxed">
                Open a 2023 archive and the layout is the layout you had on April 14th 2024.
                Amendments and IRS notices land on a familiar tree, not a reshuffled mess.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default YearEndArchive;
